/**
 * Single place for "does this user's pageAccess allow this route?" (sidebar + PageAccessRoute).
 */
import {
  isPathCoveredByBundleGrant,
  hasOnlyWorkspaceOpsGrants,
  WORKSPACE_HR_GRANT_IDS,
  WORKSPACE_OPS_GRANT_IDS,
} from '../config/pageAccessBundles';

export function isPathAllowedByPageAccess(pageAccess: string[], currentPath: string): boolean {
  if (currentPath === '/app' || currentPath === '/app/') {
    return true;
  }
  if (!pageAccess.length) {
    return false;
  }
  return pageAccess.some((granted) => {
    if (currentPath === granted) return true;
    if (isPathCoveredByBundleGrant(granted, currentPath)) return true;
    if (
      granted === '/app/partner-collections' &&
      (currentPath === '/app/partner-collections' ||
        currentPath.startsWith('/app/partner-collections/'))
    ) {
      return true;
    }
    if (
      granted === '/app/collections' ||
      granted === '/app/ledger/interac-payment-claims'
    ) {
      if (
        currentPath === '/app/collections' ||
        currentPath.startsWith('/app/collections/') ||
        currentPath === '/app/ledger/interac-payment-claims' ||
        currentPath.startsWith('/app/ledger/interac-payment-claims/')
      ) {
        return true;
      }
    }
    if (granted === '/app/ledger' && currentPath.startsWith('/app/ledger')) {
      if (currentPath === '/app/ledger/page-access' || currentPath.startsWith('/app/ledger/page-access/')) {
        return false;
      }
      return true;
    }
    if (granted === '/app/payments' && currentPath.startsWith('/app/payments')) return true;
    // Expenses (and legacy outflow) grants open the unified Transactions page; payouts stay separate.
    if (
      (granted === '/app/expenses' || granted === '/app/outflow') &&
      (currentPath === '/app/payments' || currentPath === '/app/payments/')
    ) {
      return true;
    }
    // Legacy /app/outflow grants cover Payouts + Expenses; new grants are path-specific.
    if (
      (granted === '/app/outflow' ||
        granted === '/app/payouts' ||
        granted === '/app/expenses') &&
      (currentPath.startsWith('/app/outflow') ||
        currentPath.startsWith('/app/payouts') ||
        currentPath.startsWith('/app/expenses'))
    ) {
      return true;
    }
    if (granted === '/app/legal-tasks' && currentPath.startsWith('/app/legal-tasks')) return true;
    if (granted === '/app/my-tasks' && currentPath.startsWith('/app/my-tasks')) return true;
    if (granted === '/app/my-attendance' && currentPath.startsWith('/app/my-attendance')) return true;
    if (granted === '/app/my-training' && currentPath.startsWith('/app/my-training')) return true;
    if (granted === '/app/my-policies' && currentPath.startsWith('/app/my-policies')) return true;
    if (granted === '/app/my-compliance' && currentPath.startsWith('/app/my-compliance')) return true;
    // Orders hub: do not treat /app/service-orders (or my-cases) as access to the Ops Dashboard *landing* page.
    const isOrdersDashboardRoot =
      currentPath === '/app/orders-dashboard' || currentPath === '/app/orders-dashboard/';
    if (isOrdersDashboardRoot) {
      if (granted === '/app/orders-dashboard' || granted === '/app/orders-dashboard/my-cases') {
        return true;
      }
      return false;
    }
    if (
      (granted === '/app/orders-dashboard' ||
        granted === '/app/orders-dashboard/my-cases' ||
        granted === '/app/service-orders' ||
        granted === '/app/my-cases') &&
      (currentPath.startsWith('/app/orders-dashboard/') ||
        currentPath.startsWith('/app/service-orders') ||
        currentPath.startsWith('/app/so/') ||
        currentPath.startsWith('/app/my-cases'))
    ) {
      return true;
    }
    if (granted === '/app/reports' && currentPath.startsWith('/app/reports')) {
      return true;
    }
    if (granted === '/app/whatsapp-settings' && currentPath.startsWith('/app/whatsapp-settings')) {
      return true;
    }
    if (granted === '/app/hr-attendance' && currentPath.startsWith('/app/hr-attendance')) {
      return true;
    }
    if (granted === '/app/hr-training' && currentPath.startsWith('/app/hr-training')) {
      return true;
    }
    if (granted === '/app/hr-policy' && currentPath.startsWith('/app/hr-policy')) {
      return true;
    }

    if (granted === '/app/manage-users' && currentPath.startsWith('/app/users/')) {
      return true;
    }
    if (granted === '/app/manage-users' && currentPath.startsWith('/app/hr-profiles')) {
      return true;
    }
    if (granted === '/app/departments' && currentPath.startsWith('/app/departments')) {
      return true;
    }
    if (granted === '/app/contact-center' && currentPath.startsWith('/app/contact-center')) {
      return true;
    }
    // Chats: canonical paths + legacy URLs still stored on users
    if (granted === '/app/chats-list' || granted === '/app/client-chats/list') {
      if (currentPath === '/app/chats-list' || currentPath.startsWith('/app/chats-list/')) return true;
      if (currentPath === '/app/chats-dashboard' || currentPath.startsWith('/app/chats-dashboard/')) return true;
      if (currentPath === '/app/all-community' || currentPath.startsWith('/app/all-community/')) return true;
      if (currentPath === '/app/chats-list/community' || currentPath.startsWith('/app/chats-list/community/')) {
        return true;
      }
    }
    if (granted === '/app/chats-dashboard') {
      if (currentPath === '/app/chats-dashboard' || currentPath.startsWith('/app/chats-dashboard/')) return true;
      // Chat topics: same access as chats dashboard (not a separate page-access toggle)
      if (currentPath === '/app/chat-topics' || currentPath.startsWith('/app/chat-topics/')) return true;
    }
    if (
      granted === '/app/my-chats' ||
      granted === '/app/client-chats/my-chats' ||
      granted === '/app/client-chats'
    ) {
      if (currentPath === '/app/my-chats' || currentPath.startsWith('/app/my-chats/')) return true;
      if (currentPath === '/app/my-calls' || currentPath.startsWith('/app/my-calls/')) return true;
      if (currentPath === '/app/my-chat-assignments' || currentPath.startsWith('/app/my-chat-assignments/')) return true;
      if (currentPath === '/app/my-community' || currentPath.startsWith('/app/my-community/')) return true;
      if (currentPath === '/app/my-chats/community' || currentPath.startsWith('/app/my-chats/community/')) {
        return true;
      }
    }
    if (
      granted === '/app/appointments' &&
      (currentPath === '/app/appointments' || currentPath.startsWith('/app/appointments/'))
    ) {
      return true;
    }
    if (granted === '/app/ai-assistant' && (currentPath === '/app/ai-assistant' || currentPath.startsWith('/app/ai-assistant/'))) {
      return true;
    }
    if (granted === '/app/ai-usage' && (currentPath === '/app/ai-usage' || currentPath.startsWith('/app/ai-usage/'))) {
      return true;
    }
    if (granted === '/app/shipping' && (currentPath === '/app/shipping' || currentPath.startsWith('/app/shipping/'))) {
      return true;
    }
    if (
      granted === '/app/documents' &&
      (currentPath === '/app/documents' || currentPath.startsWith('/app/documents/'))
    ) {
      return true;
    }
    if (granted === '/app/team-chat' && currentPath.startsWith('/app/team-chat')) {
      return true;
    }
    // Community — flat routes (not nested under chats-list / my-chats)
    if (granted === '/app/community') {
      if (currentPath === '/app/community' || currentPath.startsWith('/app/community/')) return true;
      if (currentPath === '/app/all-community' || currentPath.startsWith('/app/all-community/')) {
        return true;
      }
      if (currentPath === '/app/my-community' || currentPath.startsWith('/app/my-community/')) {
        return true;
      }
      // Legacy bookmarks
      if (currentPath === '/app/chats-list/community' || currentPath.startsWith('/app/chats-list/community/')) {
        return true;
      }
      if (currentPath === '/app/my-chats/community' || currentPath.startsWith('/app/my-chats/community/')) {
        return true;
      }
      if (currentPath === '/app/team-chat/community' || currentPath.startsWith('/app/team-chat/community/')) {
        return true;
      }
    }
    // Legacy: Team Chat page access still opens Community hubs until pageAccess is updated per user
    if (
      granted === '/app/team-chat' &&
      (currentPath === '/app/all-community' ||
        currentPath.startsWith('/app/all-community/') ||
        currentPath === '/app/my-community' ||
        currentPath.startsWith('/app/my-community/') ||
        currentPath === '/app/chats-list/community' ||
        currentPath.startsWith('/app/chats-list/community/') ||
        currentPath === '/app/my-chats/community' ||
        currentPath.startsWith('/app/my-chats/community/') ||
        currentPath === '/app/community' ||
        currentPath.startsWith('/app/community/'))
    ) {
      return true;
    }
    return false;
  });
}

/** Whether a user is considered to have access to a given page path (matrix / user filters). */
function doesUserHavePageAccess(
  user: { permissionRole?: string; pageAccess?: string[] },
  pagePath: string,
): boolean {
  if (user.permissionRole === 'operations_manager') return true;
  return isPathAllowedByPageAccess(user.pageAccess || [], pagePath);
}

/** Drop stale HR paths when Workspace ops is assigned without the Workspace — HR bundle. */
function pageAccessForHrEvaluation(pageAccess: string[]): string[] {
  const hasWorkspaceOps = pageAccess.some((entry) => WORKSPACE_OPS_GRANT_IDS.has(entry));
  const hasWorkspaceHrBundle = pageAccess.includes('/app/workspace-hr');
  if (hasWorkspaceOps && !hasWorkspaceHrBundle) {
    return pageAccess.filter((entry) => !WORKSPACE_HR_GRANT_IDS.has(entry));
  }
  return pageAccess;
}

/** Workspace — HR bundle or legacy per-page HR grants. Does not include Workspace (dev/IT) alone. */
function hasWorkspaceHrPageAccess(
  user?: { permissionRole?: string; pageAccess?: string[] } | null,
): boolean {
  if (!user) return false;
  const pa = pageAccessForHrEvaluation(user.pageAccess || []);

  if (!pa.length) {
    return user.permissionRole === 'operations_manager';
  }

  if (hasOnlyWorkspaceOpsGrants(pa)) {
    return false;
  }

  return (
    isPathAllowedByPageAccess(pa, '/app/workspace-hr') ||
    isPathAllowedByPageAccess(pa, '/app/manage-users') ||
    isPathAllowedByPageAccess(pa, '/app/hr-attendance') ||
    isPathAllowedByPageAccess(pa, '/app/hr-training') ||
    isPathAllowedByPageAccess(pa, '/app/hr-policy') ||
    isPathAllowedByPageAccess(pa, '/app/hr-profiles') ||
    isPathAllowedByPageAccess(pa, '/app/departments') ||
    isPathAllowedByPageAccess(pa, '/app/office-management') ||
    isPathAllowedByPageAccess(pa, '/app/workload') ||
    isPathAllowedByPageAccess(pa, '/app/workspace/page-access')
  );
}

export const LEDGER_PAGE_ACCESS_PATH = '/app/ledger/page-access';

/**
 * Check if a sub-path within the Ledger hub is permitted by the user's ledgerPageAccess list.
 *
 * Rules:
 * - Empty or missing ledgerPageAccess array means 0 subledgers allowed for non-admins (least privilege).
 * - Root Ledger hub (/app/ledger, /app/ledger/) is always accessible if user has ledger access.
 * - Matches exact path or child sub-paths (e.g. '/app/ledger/journals' matches '/app/ledger/journals/123').
 * - Handles sub-route aliases (e.g. financial-documents covers invoices & receipts).
 */
export function isLedgerSubPathAllowed(
  ledgerPageAccess?: string[] | null,
  currentPath?: string,
  permissionRole?: string | null
): boolean {
  if (!currentPath) return true;
  const normalizedPath = currentPath.split('?')[0].split('#')[0].replace(/\/+$/, '') || '/';

  // Ledger dashboard hub root is always accessible
  if (normalizedPath === '/app/ledger') {
    return true;
  }

  // Page Access Management screen requires explicit delegation grant or unrestricted admin / ops manager
  if (normalizedPath === LEDGER_PAGE_ACCESS_PATH || normalizedPath.startsWith(LEDGER_PAGE_ACCESS_PATH + '/')) {
    const role = (permissionRole || '').toLowerCase().replace(/\s+/g, '_');
    if ((role === 'admin' || role === 'operations_manager') && (ledgerPageAccess === undefined || ledgerPageAccess === null)) {
      return true;
    }
    return Array.isArray(ledgerPageAccess) && ledgerPageAccess.includes(LEDGER_PAGE_ACCESS_PATH);
  }

  // 1. Users who ALREADY had access to ledger prior to granular restrictions (ledgerPageAccess undefined/null):
  // Their work must NOT be interrupted!
  if (ledgerPageAccess === undefined || ledgerPageAccess === null) {
    return true;
  }

  // 2. Users who did NOT have access to ledger and are now granted (ledgerPageAccess: []),
  // or users whose subledgers were cleared by an admin:
  // 0 subledgers allowed by default — they need to ask admins to get access to the subledger they want.
  if (ledgerPageAccess.length === 0) {
    return false;
  }

  return ledgerPageAccess.some((granted) => {
    const cleanGranted = granted.split('?')[0].split('#')[0].replace(/\/+$/, '');
    if (normalizedPath === cleanGranted) return true;
    if (normalizedPath.startsWith(cleanGranted + '/')) return true;

    // Sub-route and alias matching
    if (
      cleanGranted === '/app/payments' ||
      cleanGranted === '/app/expenses'
    ) {
      if (normalizedPath === '/app/payments' || normalizedPath === '/app/expenses') {
        return true;
      }
    }
    if (
      cleanGranted === '/app/ledger/financial-documents' &&
      normalizedPath.startsWith('/app/ledger/financial-documents')
    ) {
      return true;
    }
    if (
      cleanGranted === '/app/ledger/payees' &&
      normalizedPath === '/app/ledger/recurring-vendor-checklist'
    ) {
      return true;
    }
    if (
      cleanGranted === '/app/ledger/gl-accounts/manage' &&
      (normalizedPath === '/app/ledger/categories' || normalizedPath.startsWith('/app/ledger/gl-accounts'))
    ) {
      return true;
    }
    if (
      cleanGranted === '/app/ledger/books/salary-slips' &&
      normalizedPath.startsWith('/app/ledger/books/salary-slips')
    ) {
      return true;
    }
    if (
      cleanGranted === '/app/ledger/vouchers' &&
      normalizedPath.startsWith('/app/ledger/vouchers')
    ) {
      return true;
    }
    if (
      cleanGranted === '/app/ledger/accounts-receivable' &&
      normalizedPath === '/app/ledger/books/accounts-receivable'
    ) {
      return true;
    }
    if (
      cleanGranted === '/app/ledger/accounts-payable' &&
      normalizedPath === '/app/ledger/books/accounts-payable'
    ) {
      return true;
    }
    if (
      (cleanGranted === '/app/ledger/open-items' || cleanGranted === '/app/ledger/accounting-exceptions') &&
      (normalizedPath === '/app/ledger/open-items' || normalizedPath === '/app/ledger/accounting-exceptions')
    ) {
      return true;
    }
    if (
      (cleanGranted === '/app/ledger/gst-forecast' ||
        cleanGranted === '/app/ledger/books/gst-hst-remittance' ||
        cleanGranted === '/app/ledger/financial-statement-generator/gst-hst') &&
      (normalizedPath === '/app/ledger/gst-forecast' ||
        normalizedPath === '/app/ledger/books/gst-hst-remittance' ||
        normalizedPath === '/app/ledger/financial-statement-generator/gst-hst')
    ) {
      return true;
    }

    return false;
  });
}

export { doesUserHavePageAccess, hasWorkspaceHrPageAccess };
